import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { COOKIE_USERNAME } from '../../../constants';
import { ChangeDetailsErrors, getRequiredChangeReasons, submitUsernameChanges } from '../../../mfcLogic/api/account';
import { logout } from '../../../mfcLogic/auth';
import { onInputBlur, onInputFocus } from '../../../state/state.layout';
import { setCookie } from '../../../utilities/cookies';
import Button from '../../Buttons/Button';
import Alert from '../../common/Alert';
import { ValidationMessageContainer } from '../../common/Forms';
import TextField from '../../common/TextField';
import { AppStateType, useAppStateSelector } from '../../Hooks/useAppState';
import { useMyAccount } from '../../Hooks/useMyAccount';
import { useUserSelector } from '../../Hooks/useUser';
import Link from '../Link';
import { formStyles, ValidationMessages } from '../Modals/Drawers/MyAccount/common';
import "./RequiredChange.linaria.module.css!=!./RequiredChange?./RequiredChange.linaria.module.css?css=H4sIAAAAAAAAA41UzZKzIBC8f0+R4+7BLYk/Me5l34RCQZyIYBCNibXv/lU2MQHXlHucpntahrE/iKlxGWCgTcHDD6caa6I5SM+oJvU/v//9nAqF2ZCbJHOrsSGUguQ/ZBQ3w4PPHD6b8zNljKpdSeVIqrlEsMK4AuEIxFyggZeuggNGp463MrpfMiWdUdNpnuAT4v05c6uxUNJ4LVxYioJnry3FXA55tXerMSN5xbXqJPVyJZROe6LfPI9qcmLae56+T42CAmcV84NqzJSmTKeoGTatEkA3jpRCD5Tphy46YHQOQyWKu9DThELXpuHzI1F8ybUfY1TG6tBdNm1D5GjYYDwigMs0Z9IwPbF1MBFfc8weo6TrI3G8cYwmsi2UrlOQYIAIa9aZLvyjmEadNMPG3ifYnkwYupWzT+462XQ2p9/Xyd2mwnAQW7dytylxlsnmizn/tkw3wVfNKJDNWw3SOwE1ZboJQ78Z3sfbzSPKu8xu5yDWPD6/F5vF/rMZihWvs9a+/ir0e4rrNl3Shufe6bkGLU5/3cnf7i8B2L/uKrSUAatGB6iyqrebriCLwWHZkOHx2pbNLgrLpLCTYwX5lSeLFsEueliIYkcVRsVpkD37C2JbbF9bxNarZCFBjcGorUpyUH/FbCN0M5riEB0bgKiala/iEeQ1Q4gBJZcycicwOh6joR5fia5hdKX/B6Z++ePaBgAA"
const titleStyles = "atm_h3_idpfg4";
type Fields = 'password' | 'confirm_password';
const rootStyles = "atm_lo_exct8b atm_le_exct8b atm_lk_exct8b atm_ll_exct8b atm_gi_1wugsn5 atm_c8_w1gvyb atm_2d_gnxck9 atm_3f_bke03k atm_5j_1y44olf atm_16zcr06_1h6ojuz";
const buttonContainer = "atm_r3_1h6ojuz";
const buttonStyles = "atm_t9_18uv5lq atm_gi_brf0ql atm_lo_i2wt44 atm_le_i2wt44 atm_lk_ftgil2 atm_ll_ftgil2 atm_g5dgub_ftgil2 atm_16ogmbs_i2wt44 atm_1u8s4yv_i2wt44 atm_1029z3i_exct8b atm_jikbkv_exct8b atm_754h8f_w1gvyb atm_lf7do_1fwxnve atm_1b4a1pt_1skhajo";
const changeRequirementNoticeStyles = "atm_2d_1qpii5k atm_7l_1qq5xm";
const RequiredChange = () => {
  const changeUsername = useAppStateSelector(AppStateType.APP, 'requireUsernameChange');
  const changePassword = useAppStateSelector(AppStateType.APP, 'requirePasswordChange');
  const myName = useUserSelector('me', 'name');
  const [reasons, setReasons] = useState<Record<number, string>>({});
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState('');
  const [desiredUsername, setDesiredUsername] = useState<string>();
  const [desiredPassword, setDesiredPassword] = useState<string>();
  const [usernameValid, setUsernameValid] = useState<boolean>(false);
  const [passwordValid, setPasswordValid] = useState<boolean>(false);
  const [formValidationErrors, setFormValidationErrors] = useState<ChangeDetailsErrors>();
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>();
  const onLogout = () => {
    logout();
  };
  const onContinue = () => {
    location.reload();
  };
  const {
    isValidUsername,
    isValidPassword,
    messages: errors,
    username,
    updatePassword,
    updateUsername,
    waitingForValidation,
    validUsername,
    validPassword
  } = useMyAccount();
  const passwordRef = useRef({
    upcoming: newPassword,
    confirmation: newPasswordConfirmation
  });
  const usernameRef = useRef(username);
  const setPasswordState = useCallback((field: Fields, value: string) => {
    switch (field) {
      case 'password':
        return setNewPassword(value);
      case 'confirm_password':
        return setNewPasswordConfirmation(value);
    }
  }, []);
  useEffect(() => {
    if (validUsername !== undefined) setUsernameValid(validUsername);
  }, [validUsername]);
  useEffect(() => {
    if (validPassword !== undefined) setPasswordValid(validPassword);
  }, [validPassword]);
  useEffect(() => {
    if (!username) return;
    const searchDebounce = setTimeout(() => {
      isValidUsername(username);
      setDesiredUsername(username);
    }, 1000);
    return () => clearTimeout(searchDebounce);
  }, [username]);
  useEffect(() => {
    if (!newPasswordConfirmation) return;
    const searchDebounce = setTimeout(() => {
      isValidPassword(newPasswordConfirmation);
      setDesiredPassword(newPasswordConfirmation);
    }, 1000);
    return () => clearTimeout(searchDebounce);
  }, [newPasswordConfirmation]);
  useEffect(() => {
    if (!showSuccessMessage) return;
    const timeout = setTimeout(() => onContinue(), 30000);
    return () => {
      clearTimeout(timeout);
    };
  }, [showSuccessMessage]);
  const onUsernameBlur = useCallback(event => {
    onInputBlur();
    isValidUsername(event?.target?.value);
    usernameRef.current = username;
  }, [username, errors]);
  const onUsernameChange = useCallback(event => {
    const {
      target
    } = event;
    const {
      value
    } = target;
    if (usernameRef.current === value) return;
    updateUsername(value);
    usernameRef.current = value;
  }, []);
  const onPasswordChange = useCallback((field: Fields) => event => {
    const {
      target
    } = event;
    const {
      value
    } = target;
    if (value === 'confirm_password') updatePassword();
    setPasswordState(field, value);
  }, []);
  const onPasswordBlur = useCallback(event => () => {
    onInputBlur();
    const {
      upcoming,
      confirmation
    } = passwordRef.current;
    if (upcoming !== confirmation) return;
    isValidPassword(event?.target?.value);
    passwordRef.current = {
      ...passwordRef.current,
      confirmation: newPasswordConfirmation
    };
  }, [errors, newPassword, newPasswordConfirmation]);
  const onSubmitRequiredChanges = useCallback(async event => {
    event.preventDefault();
    const changeDetails = await submitUsernameChanges({
      ...(changeUsername ? {
        desired_username: desiredUsername
      } : {}),
      ...(changePassword ? {
        desired_password: desiredPassword
      } : {})
    });
    if (changeDetails.err === 0) {
      setFormValidationErrors(null);
      setShowSuccessMessage(true);
      if (changeUsername && desiredUsername) setCookie(COOKIE_USERNAME, desiredUsername);
      return true;
    } else {
      setFormValidationErrors(changeDetails.errmsg);
      return false;
    }
  }, [changeUsername, changePassword, desiredUsername, desiredPassword]);
  useEffect(() => {
    async function fetchData() {
      const changeRequirementReasons = await getRequiredChangeReasons();
      setReasons(changeRequirementReasons.result);
    }
    fetchData();
  }, []);
  const title = changeUsername && changePassword ? 'New Username and Password Required' : `New ${changeUsername ? 'Username' : 'Password'} Required`;
  const doPasswordsMatch = newPassword === newPasswordConfirmation;
  const enableSubmission = useMemo(() => {
    let enableSubmit = false;
    if (changeUsername && !changePassword) {
      if (usernameValid) enableSubmit = true;
    }
    if (changePassword && !changeUsername) {
      if (passwordValid && doPasswordsMatch) enableSubmit = true;
    }
    if (changeUsername && changePassword) {
      if (usernameValid && passwordValid && doPasswordsMatch) enableSubmit = true;
    }
    return enableSubmit;
  }, [changePassword, changeUsername, doPasswordsMatch, usernameValid, passwordValid]);
  return <div data-testid="Container__RequiredChange" className={rootStyles}>
            <h3 className={titleStyles}>{title}</h3>
            {changeUsername && <>
                    <p>
                        Your current username is <strong>{myName}</strong>.
                    </p>
                    <div>
                        You must select a new username before continuing for the
                        following reason(s):
                        <ul data-testid="RequiredUsernameChange__Reason" className={changeRequirementNoticeStyles}>
                            <li>
                                <strong>{reasons['1']}</strong>
                            </li>
                        </ul>
                        <form noValidate data-testid="RequiredChange__ChangeUsernameForm" className={formStyles}>
                            <TextField label="New Username" placeholder="Enter your new username" type="text" autoComplete="username" onChange={onUsernameChange} onFocus={onInputFocus} onBlur={onUsernameBlur} required fullWidth spaced />
                            <ValidationMessageContainer>
                                {!!username && <ValidationMessages messages={errors} loading={waitingForValidation} successMessage={'Username is available!'} target="username" />}
                            </ValidationMessageContainer>
                        </form>
                    </div>
                </>}
            {changePassword && <>
                    <div>
                        You must select a new password before continuing for the
                        following reason(s):
                        <ul data-testid="RequiredPasswordChange__Reason" className={changeRequirementNoticeStyles}>
                            <li>
                                <strong>{reasons['2']}</strong>
                            </li>
                        </ul>
                        <form noValidate data-testid="MyAccount__ChangePasswordForm" className={formStyles}>
                            <TextField label="Password" type="password" autoComplete="current-password" onChange={onPasswordChange('password')} onBlur={onPasswordBlur('password')} onFocus={onInputFocus} required fullWidth spaced />
                            <TextField label="Confirm Password" type="password" autoComplete="new-password" onChange={onPasswordChange('confirm_password')} onBlur={onPasswordBlur('confirm_password')} onFocus={onInputFocus} required fullWidth spaced />
                            <ValidationMessageContainer>
                                {!!newPasswordConfirmation && doPasswordsMatch && <ValidationMessages messages={errors} loading={waitingForValidation} successMessage={'New password is ok'} target="password" />}
                                {!doPasswordsMatch && <ValidationMessages messages={{
              username: [],
              password: ['Passwords do not match.']
            }} loading={false} target="password" />}
                            </ValidationMessageContainer>
                        </form>
                    </div>
                </>}
            {!showSuccessMessage && <div className={buttonContainer}>
                    <Button onClick={onSubmitRequiredChanges} data-testid="Button__SubmitChanges" size="medium" disabled={!enableSubmission} className={buttonStyles}>
                        Submit
                    </Button>
                </div>}
            {!showSuccessMessage && formValidationErrors && <Alert severity="error">
                    We encountered an error. Please try again.
                </Alert>}
            {showSuccessMessage && <>
                    <Alert severity="success">
                        Your account details have been updated.
                        <br /> Thank you!
                    </Alert>
                    <div className={buttonContainer}>
                        <Button onClick={onContinue} data-testid="Button_Continue" size="medium" className={buttonStyles}>
                            Continue
                        </Button>
                        <em>
                            <br />
                            This page will automatically reload within 30
                            seconds
                        </em>
                    </div>
                </>}
            {!showSuccessMessage && <div>
                    <span>
                        If you wish to do this at a later time, you may instead{' '}
                        <Link actionLink inline mfc onClick={onLogout}>
                            log out
                        </Link>
                        .
                    </span>
                </div>}
        </div>;
};
export default RequiredChange;